import { useState, type FormEvent } from 'react'
import { Button } from '@/shared/ui'
import { useAdminAddMinutes, useAdminMinutes, useAdminSetTestMinutes } from '@/entities/admin'
import { formatMinSec, minutesLevel, usedShare, wholeMinutes } from '@/shared/lib/minutes'
import { AdminRemoteState } from '../shared/AdminRemoteState'
import { formatAdminDate, formatNumber } from '../shared/format'
import styles from '../AdminPages.module.css'

/**
 * Пакет минут компании.
 *
 * Выдача — запись в журнале, а не правка счётчика: видно, кто, когда и
 * сколько выдал. Ошибку исправляют отрицательной записью с причиной.
 */
export function CompanyMinutesTab({ companyId }: { companyId: number }) {
  const minutes = useAdminMinutes(companyId)
  const add = useAdminAddMinutes(companyId)
  const setTest = useAdminSetTestMinutes(companyId)
  const [amount, setAmount] = useState('1000')
  const [note, setNote] = useState('')
  const [testLimit, setTestLimit] = useState<string | null>(null)

  if (minutes.isPending) return <AdminRemoteState kind="loading" />
  if (minutes.isError || !minutes.data) {
    return (
      <AdminRemoteState
        kind="error"
        message="Не удалось загрузить пакет минут."
        onRetry={() => void minutes.refetch()}
      />
    )
  }
  const data = minutes.data
  const level = data.limited ? minutesLevel(data.usedMs, data.totalMinutes) : 'ok'
  const share = Math.min(1, usedShare(data.usedMs, data.totalMinutes))
  const testValue = testLimit ?? String(data.testDailyMinutes)

  function submitAdd(event: FormEvent) {
    event.preventDefault()
    const value = Number(amount)
    if (!Number.isInteger(value) || value === 0) return
    const action = value > 0 ? `Выдать ${value} мин` : `Снять ${-value} мин`
    if (!window.confirm(`${action}? Клиент сразу увидит новый остаток.`)) return
    add.mutate(
      { minutes: value, note: note.trim() },
      { onSuccess: () => setNote('') },
    )
  }

  function submitTest(event: FormEvent) {
    event.preventDefault()
    const value = Number(testValue)
    if (!Number.isInteger(value) || value < 0 || value > 600) return
    setTest.mutate(value, { onSuccess: () => setTestLimit(null) })
  }

  const amountValue = Number(amount)
  const amountValid = Number.isInteger(amountValue) && amountValue !== 0 && Math.abs(amountValue) <= 100000
  const needNote = amountValid && amountValue < 0 && !note.trim()

  return (
    <>
      <div className={styles.metrics}>
        <Metric
          label="Пакет"
          value={data.limited ? `${formatNumber(data.totalMinutes)} мин` : 'Нет'}
          hint={data.limited ? undefined : 'Звонки не ограничены'}
        />
        <Metric
          label="Израсходовано"
          value={`${formatNumber(wholeMinutes(data.usedMs))} мин`}
          hint={formatMinSec(data.usedMs)}
        />
        <Metric
          label="Осталось"
          value={data.limited ? formatMinSec(data.leftMs) : '—'}
          hint={data.exhausted ? 'Пакет исчерпан: обзвон стоит' : 'мин:сек'}
          tone={level}
        />
        <Metric
          label="Тесты сегодня"
          value={formatMinSec(data.testUsedTodayMs)}
          hint={data.testDailyMinutes > 0 ? `из ${data.testDailyMinutes} мин в день` : 'без лимита'}
        />
      </div>

      {data.limited && (
        <div
          className={styles.minutesBar}
          role="progressbar"
          aria-label="Израсходовано пакета"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(share * 100)}
        >
          <span
            className={`${styles.minutesFill} ${level === 'danger' ? styles.minutesDanger : level === 'warn' ? styles.minutesWarn : ''}`}
            style={{ width: `${share * 100}%` }}
          />
        </div>
      )}

      <div className={styles.twoColumns}>
        <form className={styles.panel} onSubmit={submitAdd}>
          <h2 className={styles.panelTitle}>Выдать минуты</h2>
          <p className={styles.panelHint}>
            Списываются только телефонные разговоры, посекундно с момента ответа. Недозвоны и
            разговоры, где агент молчал из-за сбоя у нас, не списываются.
          </p>
          <div className={styles.formGrid}>
            <label className={styles.field}>
              <span className={styles.label}>Минут (минус — снять)</span>
              <input
                className={styles.input}
                type="number"
                inputMode="numeric"
                step={1}
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
            </label>
            <label className={styles.field}>
              <span className={styles.label}>Комментарий</span>
              <input
                className={styles.input}
                maxLength={300}
                placeholder="Например: оплата по счёту № 12"
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
            </label>
          </div>
          {needNote && <p className={styles.errorText}>Напишите, почему снимаете минуты.</p>}
          <div className={styles.formActions}>
            <Button type="submit" size="sm" loading={add.isPending} disabled={!amountValid || needNote}>
              {amountValid && amountValue < 0 ? 'Снять минуты' : 'Выдать минуты'}
            </Button>
          </div>
        </form>

        <form className={styles.panel} onSubmit={submitTest}>
          <h2 className={styles.panelTitle}>Тестовые звонки</h2>
          <p className={styles.panelHint}>
            Звонки из браузера в пакет не входят, но тратят синтез. Лимит в минутах на день по
            часам компании; 0 — без лимита.
          </p>
          <div className={styles.formGrid}>
            <label className={styles.field}>
              <span className={styles.label}>Минут в день</span>
              <input
                className={styles.input}
                type="number"
                inputMode="numeric"
                min={0}
                max={600}
                step={1}
                value={testValue}
                onChange={(event) => setTestLimit(event.target.value)}
              />
            </label>
          </div>
          <div className={styles.formActions}>
            <Button
              type="submit"
              size="sm"
              variant="secondary"
              loading={setTest.isPending}
              disabled={testValue === String(data.testDailyMinutes)}
            >
              Сохранить лимит
            </Button>
          </div>
        </form>
      </div>

      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>История выдач</h2>
        {data.packages.length === 0 ? (
          <p className={styles.muted}>Минут ещё не выдавали — звонки компании не ограничены.</p>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Когда</th>
                  <th>Минут</th>
                  <th>Комментарий</th>
                </tr>
              </thead>
              <tbody>
                {data.packages.map((item) => (
                  <tr key={item.id}>
                    <td>{formatAdminDate(item.createdAt)}</td>
                    <td className={item.minutes < 0 ? styles.errorText : undefined}>
                      {item.minutes > 0 ? '+' : ''}
                      {formatNumber(item.minutes)}
                    </td>
                    <td className={item.note ? undefined : styles.muted}>{item.note || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  )
}

function Metric({
  label,
  value,
  hint,
  tone = 'ok',
}: {
  label: string
  value: string
  hint?: string
  tone?: 'ok' | 'warn' | 'danger'
}) {
  return (
    <article
      className={`${styles.metric} ${tone === 'danger' ? styles.metricDanger : tone === 'warn' ? styles.metricWarn : ''}`}
    >
      <span className={styles.metricLabel}>{label}</span>
      <strong className={styles.metricValue} data-admin-value>
        {value}
      </strong>
      {hint && <span className={styles.metricHint}>{hint}</span>}
    </article>
  )
}
